import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { checkVerifiedSession } from '@/lib/api';

/**
 * VerifiedRoute — gates the /quote intake app behind a valid SMS-verification
 * session token. Anyone landing without a valid token (direct nav, expired
 * token, tampered token) is bounced to /get-quote with the original
 * destination preserved.
 *
 * Token validation is delegated to the backend (`GET /api/verify/session`)
 * — a JWT signature/expiry check on the server, not just URL-presence.
 *
 * Behavior matrix:
 *   token absent         → redirect /get-quote?redirect=...&src=direct_quote
 *   token present + 200  → render <children/>
 *   token present + 4xx  → redirect /get-quote?redirect=...&src=expired_token
 *   network error        → fail-closed; redirect /get-quote?...&src=verify_error
 */
export default function VerifiedRoute({ children }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('verified') || '';
  const incomingSrc = params.get('src') || '';

  const [state, setState] = useState({
    status: token ? 'checking' : 'no_token',
    valid: false,
  });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ status: 'no_token', valid: false });
      return () => {};
    }
    setState({ status: 'checking', valid: false });
    checkVerifiedSession(token)
      .then((resp) => {
        if (cancelled) return;
        if (resp?.data?.verified) {
          setState({ status: 'ok', valid: true });
        } else {
          setState({ status: 'invalid', valid: false });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'invalid', valid: false });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'checking') {
    return (
      <div
        data-testid="verify-route-loading"
        className="min-h-screen flex flex-col items-center justify-center bg-background gap-3 px-6 text-center"
      >
        <Loader2 className="h-7 w-7 animate-spin text-[var(--brand-ocean-700)]" aria-hidden="true" />
        <p className="text-sm text-[var(--brand-slate-600)]">Verifying your session…</p>
      </div>
    );
  }

  if (state.status === 'ok') {
    return children;
  }

  // Not verified — bounce to /get-quote, preserving destination + reason.
  // Reason is encoded into `src` so analytics/GHL can see the entry path.
  const reason =
    state.status === 'no_token' ? 'direct_quote' : 'expired_token';
  const dest = location.pathname + (location.search || '');
  const out = new URLSearchParams();
  out.set('redirect', dest);
  // Prefer the user's original src if they had one (so attribution sticks),
  // otherwise tag the gate-bounce reason.
  out.set('src', incomingSrc || reason);
  return <Navigate to={`/get-quote?${out.toString()}`} replace />;
}
